import axios from 'axios';

// In production (monolithic deployment), use relative URL
// In development, use localhost
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
});

// Products API
export const getProducts = async (filters = {}) => {
  const params = new URLSearchParams(filters);
  const response = await api.get(`/products?${params}`);
  return response.data;
};

export const getProductById = async (id) => {
  const response = await api.get(`/products/${id}`);
  return response.data;
};

export const getFeaturedProducts = async () => {
  const response = await api.get(`/products/featured/all`);
  return response.data;
};

// Orders API
export const createOrder = async (orderData) => {
  const response = await api.post('/orders', orderData);
  return response.data;
};

export const getOrderByNumber = async (orderNumber) => {
  const response = await api.get(`/orders/${orderNumber}`);
  return response.data;
};

export const getOrderById = async (orderId) => {
  const response = await api.get(`/orders/id/${orderId}`);
  return response.data;
};

export const updateOrderRazorpayId = async (orderId, razorpayOrderId) => {
  const response = await api.patch(`/orders/${orderId}/razorpay-order`, { razorpayOrderId });
  return response.data;
};

export const updateOrderPayment = async (orderId, paymentData) => {
  const response = await api.patch(`/orders/${orderId}/payment`, paymentData);
  return response.data;
};

export const cancelOrder = async (orderId, reason) => {
  const response = await api.patch(`/orders/${orderId}/cancel`, { reason });
  return response.data;
};

export const getCustomerOrders = async (email) => {
  const response = await api.get(`/orders/customer/${email}`);
  return response.data;
};

// Payment API - Razorpay Integration
export const getRazorpayKey = async () => {
  const response = await api.get('/payment/key');
  return response.data;
};

export const createRazorpayOrder = async (amount, currency = 'INR', receipt = null, notes = {}) => {
  const response = await api.post('/payment/create-order', { 
    amount, 
    currency, 
    receipt,
    notes 
  });
  return response.data;
};

export const verifyPayment = async (paymentData) => {
  const response = await api.post('/payment/verify-payment', paymentData);
  return response.data;
};

export const fetchPaymentDetails = async (paymentId) => {
  const response = await api.get(`/payment/payment/${paymentId}`);
  return response.data;
};

export const fetchRazorpayOrder = async (orderId) => {
  const response = await api.get(`/payment/order/${orderId}`);
  return response.data;
};

export const requestRefund = async (paymentId, amount = null, notes = {}) => {
  const response = await api.post('/payment/refund', { paymentId, amount, notes });
  return response.data;
};

// Admin API
const getAdminHeaders = () => {
  const username = localStorage.getItem('adminUsername');
  const password = localStorage.getItem('adminPassword');
  return { username, password };
};

export const adminLogin = async (username, password) => {
  const response = await api.get('/admin/stats/dashboard', {
    headers: { username, password }
  });
  return response.data;
};

export const getAdminProducts = async () => {
  const response = await api.get('/admin/products', {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const createProduct = async (productData) => {
  const formData = new FormData();
  
  Object.keys(productData).forEach(key => {
    // Handle multiple image files
    if (key === 'images' && Array.isArray(productData[key])) {
      productData[key].forEach(file => {
        if (file instanceof File) {
          formData.append('images', file);
        }
      });
    } else if (key === 'variants' && Array.isArray(productData[key])) {
      formData.append('variants', JSON.stringify(productData[key]));
    } else if (key === 'existingImages' && Array.isArray(productData[key])) {
      formData.append('existingImages', JSON.stringify(productData[key]));
    } else if (productData[key] !== undefined && productData[key] !== null) {
      formData.append(key, productData[key]);
    }
  });
  
  const response = await api.post('/admin/products', formData, {
    headers: {
      ...getAdminHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const updateProduct = async (id, productData) => {
  const formData = new FormData();
  
  Object.keys(productData).forEach(key => {
    // Handle multiple image files
    if (key === 'images' && Array.isArray(productData[key])) {
      productData[key].forEach(file => {
        if (file instanceof File) {
          formData.append('images', file);
        }
      });
    } else if (key === 'variants' && Array.isArray(productData[key])) {
      formData.append('variants', JSON.stringify(productData[key]));
    } else if (key === 'existingImages' && Array.isArray(productData[key])) {
      formData.append('existingImages', JSON.stringify(productData[key]));
    } else if (key !== 'images' && productData[key] !== undefined && productData[key] !== null) {
      formData.append(key, productData[key]);
    }
  });
  
  const response = await api.put(`/admin/products/${id}`, formData, {
    headers: {
      ...getAdminHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const deleteProduct = async (id) => {
  const response = await api.delete(`/admin/products/${id}`, {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const updateProductStock = async (id, stock) => {
  const response = await api.patch(`/admin/products/${id}/stock`, { stock }, {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const getAdminOrders = async (filters = {}) => {
  const params = new URLSearchParams(filters);
  const response = await api.get(`/admin/orders?${params}`, {
    headers: getAdminHeaders()
  });
  return response.data;
};

export const updateOrderStatus = async (id, status) => {
  const response = await api.patch(`/admin/orders/${id}/status`, 
    { orderStatus: status }, 
    { headers: getAdminHeaders() }
  );
  return response.data;
};

export const getDashboardStats = async () => {
  const response = await api.get('/admin/stats/dashboard', {
    headers: getAdminHeaders()
  });
  return response.data;
};

// Health check
export const checkServerHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};
